import { axios } from '@/utils/axios';
import type {
  SignatureV2EntryTicket,
  SignatureV2Envelope,
  SignatureV2ParticipantInput,
  SignatureV2Side,
  SignatureV2Workflow,
} from '@/types/signatureV2';

const base = '/api/signature-v2';

export const signatureV2IdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const idempotencyHeaders = (key = signatureV2IdempotencyKey()) => ({ 'Idempotency-Key': key });

export async function createSignatureV2Workflow(documentId: number, idempotencyKey?: string) {
  const response = await axios.post<SignatureV2Envelope<SignatureV2Workflow>>(
    `${base}/deal-documents/${documentId}/workflows`,
    { select_mode: true },
    { headers: idempotencyHeaders(idempotencyKey) }
  );
  return response.data.data;
}

export async function getSignatureV2Workflows(dealId: number) {
  const response = await axios.get<SignatureV2Envelope<SignatureV2Workflow[]>>(`${base}/deals/${dealId}/workflows`);
  return response.data.data;
}

export async function getSignatureV2Workflow(workflowId: string) {
  const response = await axios.get<SignatureV2Envelope<SignatureV2Workflow>>(`${base}/workflows/${workflowId}`);
  return response.data.data;
}

export async function replaceSignatureV2Roster(
  workflowId: string,
  side: SignatureV2Side,
  version: number,
  signers: SignatureV2ParticipantInput[]
) {
  const response = await axios.put<SignatureV2Envelope<SignatureV2Workflow>>(
    `${base}/workflows/${workflowId}/parties/${side}/participants`,
    { expected_version: version, signers }
  );
  return response.data.data;
}

async function versionMutation(path: string, version: number, idempotencyKey?: string) {
  const response = await axios.post<SignatureV2Envelope<SignatureV2Workflow>>(
    `${base}${path}`,
    { expected_version: version },
    idempotencyKey ? { headers: idempotencyHeaders(idempotencyKey) } : undefined
  );
  return response.data.data;
}

export const signatureV2ActivationIdempotencyKey = (workflowId: string, version: number): string =>
  `signature-v2-activate:${workflowId}:${version}`;

export const confirmSignatureV2Party = (workflowId: string, side: SignatureV2Side, version: number) =>
  versionMutation(`/workflows/${workflowId}/parties/${side}/confirm`, version);

export async function confirmAndMaybeActivateSignatureV2Workflow(workflow: SignatureV2Workflow) {
  if (!workflow.viewer_side) throw new Error('Signature V2 viewer side is missing.');

  let updated = await confirmSignatureV2Party(workflow.uuid, workflow.viewer_side, workflow.version);
  if (updated.available_actions.includes('activate')) {
    updated = await activateSignatureV2Workflow(updated.uuid, updated.version);
  }
  return updated;
}

export const reopenSignatureV2Party = (workflowId: string, side: SignatureV2Side, version: number) =>
  versionMutation(`/workflows/${workflowId}/parties/${side}/reopen`, version);

export const activateSignatureV2Workflow = (workflowId: string, version: number, idempotencyKey?: string) =>
  versionMutation(
    `/workflows/${workflowId}/activate`,
    version,
    idempotencyKey || signatureV2ActivationIdempotencyKey(workflowId, version)
  );

export async function cancelSignatureV2Workflow(workflowId: string, version: number, reason: string) {
  const response = await axios.post<SignatureV2Envelope<SignatureV2Workflow>>(
    `${base}/workflows/${workflowId}/cancel`,
    { expected_version: version, reason },
    { headers: idempotencyHeaders() }
  );
  return response.data.data;
}

export const retrySignatureV2Workflow = (workflowId: string, version: number) =>
  versionMutation(`/workflows/${workflowId}/retry`, version);

export const resendSignatureV2Invitation = (workflowId: string, stepId: number, version: number) =>
  versionMutation(`/workflows/${workflowId}/steps/${stepId}/resend`, version, signatureV2IdempotencyKey());

export async function createSignatureV2EntryTicket(workflowId: string, stepId: number, version: number) {
  const response = await axios.post<SignatureV2Envelope<SignatureV2EntryTicket>>(
    `${base}/workflows/${workflowId}/steps/${stepId}/entry-tickets`,
    { expected_version: version }
  );
  return response.data.data;
}

export async function exchangeSignatureV2Invitation(token: string) {
  const response = await axios.post<SignatureV2Envelope<SignatureV2EntryTicket>>(
    `${base}/public/invitations/${encodeURIComponent(token)}/entry-tickets`,
    {},
    { skipAuthRefresh: true }
  );
  return response.data.data;
}

export async function getSignatureV2FinalDocument(workflowId: string) {
  const response = await axios.get<SignatureV2Envelope<{ id: number; name: string; size: number; download_url: string }>>(
    `${base}/workflows/${workflowId}/final-document`
  );
  return response.data.data;
}

export async function createSignatureV2WorkflowWithRoster(
  documentId: number,
  signers: SignatureV2ParticipantInput[],
  idempotencyKey?: string
) {
  const workflow = await createSignatureV2Workflow(documentId, idempotencyKey);
  if (!workflow.viewer_side) throw new Error('Signature V2 viewer side is missing.');

  return replaceSignatureV2Roster(workflow.uuid, workflow.viewer_side, workflow.version, signers);
}

export async function downloadSignatureV2FinalDocument(workflowId: string) {
  const response = await axios.get<Blob>(`${base}/workflows/${workflowId}/final-document/download`, {
    responseType: 'blob',
  });
  return response.data;
}


export type ExternalRoster = { version: number; document_name: string; expires_at: string; signers: SignatureV2ParticipantInput[] };
export const issueExternalRosterInvitation = (workflowId: string) => axios.post(`${base}/workflows/${workflowId}/roster-invitations/buyer`);
export const requestExternalRosterCode = (token: string) => axios.post<SignatureV2Envelope<{ challenge_id: string; resend_available_at: string }>>(`${base}/roster-invitations/${encodeURIComponent(token)}/code`, {}, { skipAuthRefresh: true });
export const verifyExternalRosterCode = (token: string, challenge_id: string, code: string) => axios.post<SignatureV2Envelope<{ grant: string; roster: ExternalRoster }>>(`${base}/roster-invitations/${encodeURIComponent(token)}/verify`, { challenge_id, code }, { skipAuthRefresh: true });
export const operateExternalRoster = (token: string, grant: string, action: 'view' | 'save' | 'confirm', data = {}) =>
  axios.post<SignatureV2Envelope<ExternalRoster & { confirmed?: boolean }>>(`${base}/roster-invitations/${encodeURIComponent(token)}/${action}`, { ...data, grant }, { skipAuthRefresh: true });
